import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserInfo, SellerInfo, StylistInfo } from "@/types/AppTypes";
import { CheckCircle, XCircle, Mail, Phone, MapPin, Building, Calendar, FileText, Eye, X } from "lucide-react";

type UserWithRoleInfo = (UserInfo & {role?:string} & StylistInfo) | (UserInfo & {role?:string}  & SellerInfo);

interface UserDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserWithRoleInfo | null;
  onApprove?: (user: UserWithRoleInfo) => void;
  onReject?: (userId: string) => void;
  showApprovalActions?: boolean;
  isApproving?: boolean;
}

export const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  isOpen,
  onClose,
  user,
  onApprove,
  onReject,
  showApprovalActions = false,
  isApproving = false,
}) => {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  if (!user) return null;

  const isSeller = user?.role === "Distributor";
  const isBuyer = user?.role === "Stylist";
  const isVerified = user?.is_verified ?? false;

  let userType = "Stylist";
  let badgeColor = "bg-blue-100 text-blue-800";

  if (isSeller) {
    userType = "Distributor";
    badgeColor = "bg-yellow-100 text-yellow-800";
  } else if (isBuyer) {
    userType = "Buyer";
    badgeColor = "bg-blue-100 text-blue-800";
  }

  const handleApprove =async () => {
    if (onApprove && user.user_id) {
      await onApprove(user);
      onClose();
    }
  };

//   const handleReject = () => {
//     if (onReject && user.user_id) {
//       onReject(user.user_id);
//       onClose();
//     }
//   };

  const handleImageClick = (imagePath: string) => {
    setSelectedImage(imagePath);
    setImageModalOpen(true);
  };

  const isImageFile = (path: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => path.toLowerCase().includes(ext));
  };

  const isPdfFile = (path: string) => {
    return path.toLowerCase().includes('.pdf');
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="md:max-w-6xl md:max-h-[90vh] overflow-y-auto py-10 px-8">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>User Details</span>
              <Badge variant="outline" className={badgeColor}>
                {userType}
              </Badge>
              <Badge
                variant={isVerified ? "default" : "secondary"}
                className={isVerified ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}
              >
                {isVerified ? "Verified" : "Unverified"}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Complete information about this user
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-10 mt-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Name:</span>
                  <span>{user.first_name} {user.last_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Email:</span>
                  <span>{user.email || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Phone:</span>
                  <span>{user.phone || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Created:</span>
                  <span>{user.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}</span>
                </div>
              </div>
            </div>

            {/* Business Information (for Sellers) */}
            {isSeller && 'business_name' in user && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Business Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">Business Name:</span>
                    <span>{user.business_name || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Tax ID:</span>
                    <span>{user.tax_id || "N/A"}</span>
                  </div>
                  <div className="col-span-full">
                    <span className="font-medium">Description:</span>
                    <p className="mt-1 text-sm text-gray-600">{user.description || "No description provided"}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Tax Documents (for Sellers) */}
            {isSeller && 'tax_documents' in user && user.tax_documents && user.tax_documents.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Tax Documents</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {user.tax_documents.map((docPath, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Tax Document {index + 1}:</span>
                      </div>
                      
                      {isImageFile(docPath) ? (
                        <div className="space-y-3">
                          <div className="relative inline-block">
                            <img 
                              src={docPath} 
                              alt={`Tax Document ${index + 1}`}
                              className="max-w-xs max-h-48 object-contain border rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => handleImageClick(docPath)}
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black bg-opacity-20 rounded-lg">
                              <Eye className="h-6 w-6 text-white" />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleImageClick(docPath)}
                              className="flex items-center gap-2"
                            >
                              <Eye className="h-4 w-4" />
                              View Full Size
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              asChild
                            >
                              <a 
                                href={docPath} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-2"
                              >
                                <FileText className="h-4 w-4" />
                                Open in New Tab
                              </a>
                            </Button>
                          
                          </div>
                        </div>
                      ) : isPdfFile(docPath) ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                            <FileText className="h-8 w-8 text-red-500" />
                            <span className="font-medium">PDF Document</span>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              asChild
                            >
                              <a 
                                href={docPath} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-2"
                              >
                                <FileText className="h-4 w-4" />
                                View PDF
                              </a>
                            </Button>
                         
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            asChild
                          >
                            <a 
                              href={docPath} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2"
                            >
                              <FileText className="h-4 w-4" />
                              View Document
                            </a>
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            asChild
                          >
                            <a 
                              href={docPath} 
                              download
                              className="flex items-center gap-2"
                            >
                              <FileText className="h-4 w-4" />
                              Download
                            </a>
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* License Information (for Sellers) */}
            {isSeller && 'license_path' in user && user.license_path && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">License Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">Business License:</span>
                  </div>
                  
                  {isImageFile(user.license_path) ? (
                    <div className="space-y-3">
                      <div className="relative inline-block">
                        <img 
                          src={user.license_path} 
                          alt="Business License"
                          className="max-w-xs max-h-48 object-contain border rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => handleImageClick(user.license_path!)}
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black bg-opacity-20 rounded-lg">
                          <Eye className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleImageClick(user.license_path!)}
                          className="flex items-center gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          View Full Size
                        </Button>
                                                    <Button 
                              variant="outline" 
                              size="sm"
                              asChild
                            >
                              <a 
                                href={user.license_path} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-2"
                              >
                                <FileText className="h-4 w-4" />
                                Open in New Tab
                              </a>
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              asChild
                            >
                              <a 
                                href={user.license_path} 
                                download
                                className="flex items-center gap-2"
                              >
                                <FileText className="h-4 w-4" />
                                Download
                              </a>
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              asChild
                            >
                              <a 
                                href={user.license_path} 
                                download
                                className="flex items-center gap-2"
                              >
                                <FileText className="h-4 w-4" />
                                Download
                              </a>
                            </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        asChild
                      >
                        <a 
                          href={user.license_path} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2"
                        >
                          <FileText className="h-4 w-4" />
                          View License Document
                        </a>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Salon Information (for Stylists) */}
            {!isSeller && 'saloon_name' in user && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Salon Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">Salon Name:</span>
                    <span>{user.saloon_name || "N/A"}</span>
                  </div>
                  
                  {/* License for Stylists */}
                  {user.license_path && (
                    <div className="col-span-full space-y-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Stylist License:</span>
                      </div>
                      
                      {isImageFile(user.license_path) ? (
                        <div className="space-y-3">
                          <div className="relative inline-block">
                            <img 
                              src={user.license_path} 
                              alt="Stylist License"
                              className="max-w-xs max-h-48 object-contain border rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => handleImageClick(user.license_path!)}
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black bg-opacity-20 rounded-lg">
                              <Eye className="h-6 w-6 text-white" />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleImageClick(user.license_path!)}
                              className="flex items-center gap-2"
                            >
                              <Eye className="h-4 w-4" />
                              View Full Size
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              asChild
                            >
                              <a 
                                href={user.license_path} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-2"
                              >
                                <FileText className="h-4 w-4" />
                                Open in New Tab
                              </a>
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            asChild
                          >
                            <a 
                              href={user.license_path} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2"
                            >
                              <FileText className="h-4 w-4" />
                              View License Document
                            </a>
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b pb-2">Address Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Street:</span>
                  <span>{user.street || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Suite:</span>
                  <span>{user.suite || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">City:</span>
                  <span>{user.city || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">State:</span>
                  <span>{user.state || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Country:</span>
                  <span>{user.country || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Postal Code:</span>
                  <span>{user.postal_code || "N/A"}</span>
                </div>
              </div>
            </div>

            {/* Approval Actions */}
            {showApprovalActions && !isVerified && (
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={handleApprove}
                  className="flex items-center gap-2 hover:bg-black/80 text-white"
                  variant="default"
                  disabled={isApproving}
                >
                  <CheckCircle className="h-4 w-4" />
                  {isApproving ? "Approving..." : "Approve User"}
                </Button>
                {/* <Button
                  onClick={handleReject}
                  className="flex items-center gap-2"
                  variant="destructive"
                >
                  <XCircle className="h-4 w-4" />
                  Reject User
                </Button> */}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Screen Image Modal */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="max-w-7xl max-h-[95vh] p-2">
          <DialogHeader className="sr-only">
            <DialogTitle>License Image</DialogTitle>
            <DialogDescription>Full size view of the license</DialogDescription>
          </DialogHeader>
          <div className="relative flex items-center justify-center min-h-[80vh]">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 z-10 bg-black/20 hover:bg-black/40 text-white"
              onClick={() => setImageModalOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            {selectedImage && (
              <img 
                src={selectedImage} 
                alt="License Full View"
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}; 